/**
 * Shared phone country dial-code list used by every Anderson College lead
 * form. Adding a country here automatically:
 *
 *   1. Surfaces it in every dropdown that imports PHONE_COUNTRIES.
 *   2. Validates phone length via PHONE_LENGTHS_BY_CC in formValidation.js
 *      (codes without a length here fall through to the permissive
 *      "≥ 7 digits" rule).
 *
 * Ordering rule: Canada + US first (primary market), then the top
 * Anderson admissions source countries (India, Philippines, Nigeria,
 * Pakistan, Bangladesh, Sri Lanka, Nepal — alphabetical after Canada
 * pinned to top), then alphabetical for everything else.
 */

export const PHONE_COUNTRIES = [
  // North America (primary)
  { iso: "CA",  dial: "+1",    label: "CA +1",   name: "Canada" },
  { iso: "US",  dial: "+1",    label: "US +1",   name: "United States" },

  // Top Anderson international source countries
  { iso: "IN",  dial: "+91",   label: "IN +91",  name: "India" },
  { iso: "PH",  dial: "+63",   label: "PH +63",  name: "Philippines" },
  { iso: "NG",  dial: "+234",  label: "NG +234", name: "Nigeria" },
  { iso: "PK",  dial: "+92",   label: "PK +92",  name: "Pakistan" },
  { iso: "BD",  dial: "+880",  label: "BD +880", name: "Bangladesh" },
  { iso: "LK",  dial: "+94",   label: "LK +94",  name: "Sri Lanka" },
  { iso: "NP",  dial: "+977",  label: "NP +977", name: "Nepal" },
  { iso: "GB",  dial: "+44",   label: "UK +44",  name: "United Kingdom" },

  // Rest — alphabetical
  { iso: "AE",  dial: "+971",  label: "AE +971", name: "United Arab Emirates" },
  { iso: "AU",  dial: "+61",   label: "AU +61",  name: "Australia" },
  { iso: "BR",  dial: "+55",   label: "BR +55",  name: "Brazil" },
  { iso: "CN",  dial: "+86",   label: "CN +86",  name: "China" },
  { iso: "CO",  dial: "+57",   label: "CO +57",  name: "Colombia" },
  { iso: "DE",  dial: "+49",   label: "DE +49",  name: "Germany" },
  { iso: "EG",  dial: "+20",   label: "EG +20",  name: "Egypt" },
  { iso: "ES",  dial: "+34",   label: "ES +34",  name: "Spain" },
  { iso: "FR",  dial: "+33",   label: "FR +33",  name: "France" },
  { iso: "GH",  dial: "+233",  label: "GH +233", name: "Ghana" },
  { iso: "HK",  dial: "+852",  label: "HK +852", name: "Hong Kong" },
  { iso: "ID",  dial: "+62",   label: "ID +62",  name: "Indonesia" },
  { iso: "IE",  dial: "+353",  label: "IE +353", name: "Ireland" },
  { iso: "IL",  dial: "+972",  label: "IL +972", name: "Israel" },
  { iso: "IT",  dial: "+39",   label: "IT +39",  name: "Italy" },
  { iso: "JM",  dial: "+1876", label: "JM +1876",name: "Jamaica" },
  { iso: "JP",  dial: "+81",   label: "JP +81",  name: "Japan" },
  { iso: "KE",  dial: "+254",  label: "KE +254", name: "Kenya" },
  { iso: "KR",  dial: "+82",   label: "KR +82",  name: "South Korea" },
  { iso: "MA",  dial: "+212",  label: "MA +212", name: "Morocco" },
  { iso: "MX",  dial: "+52",   label: "MX +52",  name: "Mexico" },
  { iso: "MY",  dial: "+60",   label: "MY +60",  name: "Malaysia" },
  { iso: "NL",  dial: "+31",   label: "NL +31",  name: "Netherlands" },
  { iso: "NZ",  dial: "+64",   label: "NZ +64",  name: "New Zealand" },
  { iso: "PE",  dial: "+51",   label: "PE +51",  name: "Peru" },
  { iso: "PL",  dial: "+48",   label: "PL +48",  name: "Poland" },
  { iso: "PT",  dial: "+351",  label: "PT +351", name: "Portugal" },
  { iso: "RO",  dial: "+40",   label: "RO +40",  name: "Romania" },
  { iso: "SA",  dial: "+966",  label: "SA +966", name: "Saudi Arabia" },
  { iso: "SG",  dial: "+65",   label: "SG +65",  name: "Singapore" },
  { iso: "TH",  dial: "+66",   label: "TH +66",  name: "Thailand" },
  { iso: "TR",  dial: "+90",   label: "TR +90",  name: "Turkey" },
  { iso: "TW",  dial: "+886",  label: "TW +886", name: "Taiwan" },
  { iso: "TZ",  dial: "+255",  label: "TZ +255", name: "Tanzania" },
  { iso: "UA",  dial: "+380",  label: "UA +380", name: "Ukraine" },
  { iso: "UG",  dial: "+256",  label: "UG +256", name: "Uganda" },
  { iso: "VN",  dial: "+84",   label: "VN +84",  name: "Vietnam" },
  { iso: "ZA",  dial: "+27",   label: "ZA +27",  name: "South Africa" },
];

// Legacy-shaped array kept for forms that destructured `{ label, value }`.
// Note: `value` is the dial code, NOT the ISO. Multiple countries can share
// the same dial code (CA/US/JM use +1) — this is fine because the validator
// only cares about the dial code for length checks.
export const PHONE_CODES = PHONE_COUNTRIES.map((c) => ({
  label: c.label,
  value: c.dial,
}));
